import { notify } from "@general-backoffice/core";
import type { CryptoPaymentOptions, Invoice } from "@investhinks/api-client";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";

import { cryptoPaymentConfigEndpoints, manualPaymentsEndpoints } from "../../api";
import getErrorMessage from "../../utils/getErrorMessage";
import { getCurrencyDisplay } from "../utils/currency";

interface RecordPaymentModalProps {
  isOpen: boolean;
  toggle: () => void;
  invoice: Invoice | null;
  onPaymentRecorded?: () => void;
}

interface PaymentDetails {
  amount: number;
  paymentMethod: "bank_transfer" | "check" | "cash" | "other";
  paidAt?: string;
  currency?: string;
  reference?: string;
  notes?: string;
  // Extended fields for crypto/bank (passed as extra data to API)
  transactionId?: string;
  blockchain?: string;
  cryptoCurrency?: string;
  walletAddress?: string;
  bankName?: string;
  bankAccountIban?: string;
  bankTransactionRef?: string;
}

const RecordPaymentModal: React.FC<RecordPaymentModalProps> = ({ isOpen, toggle, invoice, onPaymentRecorded }) => {
  const { t } = useTranslation("manualPayments", { keyPrefix: "recordModal" });

  // Crypto config state (loaded from backend)
  const [cryptoConfig, setCryptoConfig] = useState<CryptoPaymentOptions | null>(null);
  const [configLoading, setConfigLoading] = useState(false);

  // Form state
  const [paymentMethod, setPaymentMethod] = useState("crypto");
  const [paidAt, setPaidAt] = useState(moment().format("YYYY-MM-DD"));
  const [paidAmount, setPaidAmount] = useState("");
  const [paidCurrency, setPaidCurrency] = useState("EUR");

  // Crypto fields
  const [transactionId, setTransactionId] = useState("");
  const [blockchain, setBlockchain] = useState("");
  const [cryptoCurrency, setCryptoCurrency] = useState("");
  const [walletAddress, setWalletAddress] = useState("");

  // Bank fields
  const [bankName, setBankName] = useState("");
  const [bankAccountIban, setBankAccountIban] = useState("");
  const [bankTransactionRef, setBankTransactionRef] = useState("");

  const [adminNotes, setAdminNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load crypto config when modal opens
  const loadCryptoConfig = useCallback(async () => {
    try {
      setConfigLoading(true);
      const { data: config } = await cryptoPaymentConfigEndpoints.getOptions();
      setCryptoConfig(config);

      // Set defaults from config
      if (config?.isEnabled) {
        if (config.supportedChains?.length && config.supportedChains.length > 0) {
          setBlockchain(config.supportedChains[0]);
        }
        if (config.supportedCurrencies?.length && config.supportedCurrencies.length > 0) {
          setCryptoCurrency(config.supportedCurrencies[0]);
        }
        if (config.walletAddress) {
          setWalletAddress(config.walletAddress);
        }
      }
    } catch (err: unknown) {
      console.warn("Failed to load crypto config:", err);
    } finally {
      setConfigLoading(false);
    }
  }, []);

  // Load config when modal opens
  useEffect(() => {
    if (isOpen) {
      loadCryptoConfig();
    }
  }, [isOpen, loadCryptoConfig]);

  // Reset form when invoice changes
  useEffect(() => {
    if (invoice) {
      setPaidAmount(invoice.netPayable?.toString() || "");
      setPaidCurrency("EUR");
      setPaymentMethod("crypto");
      setTransactionId("");
      // Reset to config defaults
      if (cryptoConfig?.isEnabled) {
        setBlockchain(cryptoConfig.supportedChains?.[0] || "");
        setCryptoCurrency(cryptoConfig.supportedCurrencies?.[0] || "");
        setWalletAddress(cryptoConfig.walletAddress || "");
      } else {
        setBlockchain("");
        setCryptoCurrency("");
        setWalletAddress("");
      }
      setBankName("");
      setBankAccountIban("");
      setBankTransactionRef("");
      setAdminNotes("");
      setError(null);
    }
  }, [invoice, cryptoConfig]);

  const handleSubmit = async () => {
    if (!invoice) return;

    setError(null);

    // Map UI payment method to API-compatible value ("crypto" maps to "other")
    const apiPaymentMethod: PaymentDetails["paymentMethod"] =
      paymentMethod === "bank_transfer"
        ? "bank_transfer"
        : paymentMethod === "check"
          ? "check"
          : paymentMethod === "cash"
            ? "cash"
            : "other";

    const paymentDetails: PaymentDetails = {
      amount: parseFloat(paidAmount),
      paymentMethod: apiPaymentMethod,
      paidAt: new Date(paidAt).toISOString(),
      currency: paidCurrency,
      notes: adminNotes || undefined,
    };

    if (paymentMethod === "crypto") {
      paymentDetails.transactionId = transactionId;
      paymentDetails.blockchain = blockchain;
      paymentDetails.cryptoCurrency = cryptoCurrency;
      paymentDetails.walletAddress = walletAddress; // Company wallet that received payment
    } else {
      paymentDetails.bankName = bankName;
      paymentDetails.bankAccountIban = bankAccountIban;
      paymentDetails.bankTransactionRef = bankTransactionRef;
    }

    try {
      setIsSubmitting(true);
      // Build reference string from payment-method-specific fields
      const referenceDetails =
        paymentMethod === "crypto"
          ? [transactionId, blockchain, cryptoCurrency, walletAddress].filter(Boolean).join(" | ")
          : [bankTransactionRef, bankName, bankAccountIban].filter(Boolean).join(" | ");

      await manualPaymentsEndpoints.recordPayment(invoice.id, {
        amount: paymentDetails.amount,
        paymentMethod: paymentDetails.paymentMethod,
        paidAt: paymentDetails.paidAt,
        currency: paymentDetails.currency,
        notes: paymentDetails.notes,
        reference: referenceDetails || undefined,
      });
      notify.success(t("success"));
      if (onPaymentRecorded) onPaymentRecorded();
      toggle();
    } catch (err: unknown) {
      setError(getErrorMessage(err, t("error")));
    } finally {
      setIsSubmitting(false);
    }
  };

  const parsedAmount = Number.parseFloat(paidAmount);
  const isAmountValid = Number.isFinite(parsedAmount) && parsedAmount > 0;
  // A payment recorded in a different unit than the invoice, or diverging from
  // the amount due, should be a deliberate choice rather than a typo.
  const invoiceCurrency = invoice?.currency || "EUR";
  const currencyMismatch = paidCurrency !== invoiceCurrency;
  const amountMismatch =
    isAmountValid &&
    invoice?.netPayable != null &&
    !currencyMismatch &&
    Math.abs(parsedAmount - invoice.netPayable) > 0.01;

  const isFormValid = () => {
    if (!paidAt || !isAmountValid || !paidCurrency) return false;
    if (paymentMethod === "crypto") {
      if (!transactionId) return false;
      if (!cryptoConfig?.isEnabled) return false;
      if (!blockchain || !cryptoCurrency) return false;
    }
    if (paymentMethod === "bank_transfer" && !bankTransactionRef) return false;
    return true;
  };

  if (!invoice) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>{t("title")}</ModalHeader>
      <ModalBody>
        {/* Invoice Info Alert */}
        <Alert color="info" className="mb-4">
          <strong>{t("invoiceInfo")}</strong>
          <div>
            {t("user")}: {invoice.user?.name || invoice.user?.email}
          </div>
          <div>
            {t("invoiceNumber")}: {invoice.invoiceNumber}
          </div>
          <div>
            {t("amount")}: {invoice.netPayable?.toFixed(2)} {getCurrencyDisplay(invoice.currency)}
          </div>
          {invoice.dueDate && (
            <div>
              {t("dueDate")}: {moment(invoice.dueDate).format("DD/MM/YYYY")}
            </div>
          )}
        </Alert>

        {error && (
          <Alert color="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {(currencyMismatch || amountMismatch) && (
          <Alert color="warning" className="mb-3" role="alert">
            <i className="fas fa-exclamation-triangle mr-2" aria-hidden="true" />
            {currencyMismatch
              ? t("currencyMismatchWarning", {
                  paid: paidCurrency,
                  invoice: getCurrencyDisplay(invoiceCurrency),
                })
              : t("amountMismatchWarning", {
                  paid: parsedAmount.toFixed(2),
                  due: invoice.netPayable?.toFixed(2),
                })}
          </Alert>
        )}

        {/* Payment Method */}
        <FormGroup>
          <Label for="mp-payment-method">{t("paymentMethod")}</Label>
          <Input
            id="mp-payment-method"
            type="select"
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
          >
            <option value="crypto">{t("methods.crypto")}</option>
            <option value="bank_transfer">{t("methods.bankTransfer")}</option>
          </Input>
        </FormGroup>

        {/* Common Fields */}
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="mp-paid-at">{t("paidAt")}</Label>
              <Input id="mp-paid-at" type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
            </FormGroup>
          </Col>
          <Col md={3}>
            <FormGroup>
              <Label for="mp-paid-amount">{t("paidAmount")}</Label>
              <Input
                id="mp-paid-amount"
                type="number"
                step="0.01"
                min="0.01"
                invalid={!!paidAmount && !isAmountValid}
                value={paidAmount}
                onChange={(e) => setPaidAmount(e.target.value)}
              />
            </FormGroup>
          </Col>
          <Col md={3}>
            <FormGroup>
              <Label for="mp-paid-currency">{t("paidCurrency")}</Label>
              <Input
                id="mp-paid-currency"
                type="select"
                value={paidCurrency}
                onChange={(e) => setPaidCurrency(e.target.value)}
              >
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="USDT">USDT</option>
                <option value="USDC">USDC</option>
                <option value="BTC">BTC</option>
                <option value="ETH">ETH</option>
              </Input>
            </FormGroup>
          </Col>
        </Row>

        {/* Crypto Fields */}
        {paymentMethod === "crypto" && (
          <>
            {/* Crypto Config Info Card */}
            {cryptoConfig?.isEnabled && cryptoConfig?.walletAddress && (
              <Card className="mb-3 bg-light border">
                <CardBody className="py-3">
                  <Row className="align-items-center">
                    <Col md={cryptoConfig.qrCodeDataUrl ? 8 : 12}>
                      <div className="mb-2">
                        <small className="text-muted d-block">{t("companyWallet", "Company Wallet Address")}</small>
                        <code className="text-dark" style={{ fontSize: "0.8rem", wordBreak: "break-all" }}>
                          {cryptoConfig.walletAddress}
                        </code>
                      </div>
                      {cryptoConfig.notes && (
                        <small className="text-muted">
                          <i className="fas fa-info-circle mr-1" />
                          {cryptoConfig.notes}
                        </small>
                      )}
                    </Col>
                    {cryptoConfig.qrCodeDataUrl && (
                      <Col md={4} className="text-center">
                        <img
                          src={cryptoConfig.qrCodeDataUrl}
                          alt="Wallet QR"
                          style={{
                            width: 80,
                            height: 80,
                            border: "2px solid #dee2e6",
                            borderRadius: 4,
                          }}
                        />
                      </Col>
                    )}
                  </Row>
                </CardBody>
              </Card>
            )}

            {!cryptoConfig?.isEnabled && !configLoading && (
              <Alert color="warning" className="mb-3">
                <i className="fas fa-exclamation-triangle mr-2" />
                {t(
                  "cryptoNotConfigured",
                  "Crypto payments are not configured. Please set up crypto payment settings first."
                )}
              </Alert>
            )}

            {configLoading && (
              <div className="text-center py-3">
                <Spinner size="sm" />
                <small className="text-muted ml-2">{t("loadingConfig", "Loading crypto config...")}</small>
              </div>
            )}

            <FormGroup>
              <Label for="record-payment-modal-field-1">
                {t("transactionId")} <span className="text-danger">*</span>
              </Label>
              <Input id="record-payment-modal-field-1"
                type="text"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                placeholder={t("transactionIdPlaceholder")}
              />
            </FormGroup>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="record-payment-modal-field-2">{t("blockchain")}</Label>
                  <Input id="record-payment-modal-field-2"
                    type="select"
                    value={blockchain}
                    onChange={(e) => setBlockchain(e.target.value)}
                    disabled={!cryptoConfig?.supportedChains?.length}
                  >
                    {(cryptoConfig?.supportedChains?.length ?? 0) > 0 ? (
                      (cryptoConfig?.supportedChains ?? []).map((chain) => (
                        <option key={chain} value={chain}>
                          {chain.charAt(0).toUpperCase() + chain.slice(1)}
                        </option>
                      ))
                    ) : (
                      <option value="">{t("noChains", "No chains configured")}</option>
                    )}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="record-payment-modal-field-3">{t("cryptoCurrency")}</Label>
                  <Input id="record-payment-modal-field-3"
                    type="select"
                    value={cryptoCurrency}
                    onChange={(e) => setCryptoCurrency(e.target.value)}
                    disabled={!cryptoConfig?.supportedCurrencies?.length}
                  >
                    {(cryptoConfig?.supportedCurrencies?.length ?? 0) > 0 ? (
                      (cryptoConfig?.supportedCurrencies ?? []).map((currency) => (
                        <option key={currency} value={currency}>
                          {currency}
                        </option>
                      ))
                    ) : (
                      <option value="">{t("noCurrencies", "No currencies configured")}</option>
                    )}
                  </Input>
                </FormGroup>
              </Col>
            </Row>
          </>
        )}

        {/* Bank Transfer Fields */}
        {paymentMethod === "bank_transfer" && (
          <>
            <FormGroup>
              <Label for="record-payment-modal-field-4">
                {t("bankTransactionRef")} <span className="text-danger">*</span>
              </Label>
              <Input id="record-payment-modal-field-4"
                type="text"
                value={bankTransactionRef}
                onChange={(e) => setBankTransactionRef(e.target.value)}
                placeholder={t("bankTransactionRefPlaceholder")}
              />
            </FormGroup>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="record-payment-modal-field-5">{t("bankName")}</Label>
                  <Input id="record-payment-modal-field-5" type="text" value={bankName} onChange={(e) => setBankName(e.target.value)} />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="record-payment-modal-field-6">{t("bankAccountIban")}</Label>
                  <Input id="record-payment-modal-field-6" type="text" value={bankAccountIban} onChange={(e) => setBankAccountIban(e.target.value)} />
                </FormGroup>
              </Col>
            </Row>
          </>
        )}

        {/* Admin Notes */}
        <FormGroup>
          <Label for="record-payment-modal-field-7">{t("adminNotes")}</Label>
          <Input id="record-payment-modal-field-7"
            type="textarea"
            rows={3}
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder={t("adminNotesPlaceholder")}
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={isSubmitting}>
          {t("cancel")}
        </Button>
        <Button color="success" onClick={handleSubmit} disabled={isSubmitting || !isFormValid()}>
          {isSubmitting ? (
            <>
              <Spinner size="sm" className="mr-1" /> {t("submitting")}
            </>
          ) : (
            <>
              <i className="fas fa-check mr-1" /> {t("confirm")}
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default RecordPaymentModal;
